import { getWorkspaceIdFromCurrentUrl } from '@/context/current-workspace';
import { toggle } from '@/features/sidebar';
import { IconButton } from '@/layouts/authenticated/components/top-navigation/components/icon-button';
import { servicesApi } from '@/services/api';
import { servicesApi as untypedServicesApi } from '@/services/api/deprecated';
import { AccountFeatureFlag, useFeatureFlag } from '@/services/auth/feature-flags';
import { AiEnabledGuard } from '@/components/guards';
import { authStore } from '@/services/auth/store';
import {
  setCopilotConfig,
  type APICopilotGetFileDownloadUrl,
  type APICopilotGetUploadSignature,
  type CopilotUploadSignatureResponse,
} from '@customerio/hydra-copilot';
import { SidebarChat } from './chat';
import { History } from './history';
import { processStreamingLines } from './helpers/process-streaming-lines';
import { useIsAgentFullscreen } from './hooks';

setCopilotConfig({
  async startSession() {
    const auth = authStore.get();
    const accountId = auth?.account?.id;

    if (accountId == null) {
      throw new Error('No account ID found in auth store');
    }

    const response = await servicesApi.createCopilotSession({
      params: { accountId },
      body: {},
    });

    return {
      id: response.session_id,
      tools: response.tools,
    };
  },
  getUploadSignature: (async (
    payload: Parameters<APICopilotGetUploadSignature>[0],
  ): Promise<CopilotUploadSignatureResponse> => {
    const auth = authStore.get();
    const accountId = auth?.account?.id;

    if (accountId == null) {
      throw new Error('No account ID found in auth store');
    }

    const response = await servicesApi.getCopilotFileUploadSignature({
      params: {
        accountId,
        sessionId: payload.id,
      },
      body: {
        size: payload.size,
        mime_type: payload.mime_type,
      },
    });

    return response as CopilotUploadSignatureResponse;
  }) satisfies APICopilotGetUploadSignature,
  getFileDownloadUrl: (async (payload: Parameters<APICopilotGetFileDownloadUrl>[0]) => {
    const auth = authStore.get();
    const accountId = auth?.account?.id;

    if (accountId == null) {
      throw new Error('No account ID found in auth store');
    }

    const response = await servicesApi.getCopilotFileDownloadUrl({
      params: {
        accountId,
        sessionId: payload.id,
        filename: payload.filename,
      },
    });

    return {
      url: response.url,
    };
  }) satisfies APICopilotGetFileDownloadUrl,
  async getSession(id) {
    const auth = authStore.get();
    const accountId = auth?.account?.id;

    if (accountId == null) {
      throw new Error('No account ID found in auth store');
    }

    const response = await servicesApi.getCopilotSession({
      params: { accountId, sessionId: Number(id) },
    });

    return {
      meta: { name: response.name },
      messages: response.messages,
      tools: response.tools,
    };
  },
  async sendStreamingMessage({ onMessage, id, signal, ...payload }) {
    const state = { currentLine: -1 };
    const auth = authStore.get();
    const accountId = auth?.account?.id;

    if (accountId == null) {
      throw new Error('No account ID found in auth store');
    }

    await untypedServicesApi.post(
      `/accounts/${accountId}/copilot/${id}`,
      {
        environment_id: getWorkspaceIdFromCurrentUrl(),
        stream: true,
        ...payload,
      },
      {
        signal,
        onDownloadProgress: (progressEvent) => {
          const response = progressEvent.event.currentTarget.response;
          processStreamingLines(response, state, onMessage);
        },
      },
    );
  },
  async stopExchange({ id }) {
    const auth = authStore.get();
    const accountId = auth?.account?.id;

    if (accountId == null) {
      throw new Error('No account ID found in auth store');
    }

    await untypedServicesApi.post(`/accounts/${accountId}/copilot/${id}/stop`, {});
  },
  async endSession() {
    // noop
  },
  async generate(payload) {
    const auth = authStore.get();
    const accountId = auth?.account?.id;

    if (accountId == null) {
      throw new Error('No account ID found in auth store');
    }

    const { data } = await untypedServicesApi.post(`/accounts/${accountId}/copilot_generate`, {
      ...payload,
      environment_id: getWorkspaceIdFromCurrentUrl(),
    });

    return data;
  },
});

export function Copilot(props?: { isNewLayout?: boolean; size?: 'sm' | 'md' }) {
  const isAgentFullscreen = useIsAgentFullscreen();
  const size = props?.size ?? 'sm';
  const isV2 = useFeatureFlag(AccountFeatureFlag.AiAssistantV2);

  if (isAgentFullscreen) {
    return null;
  }

  return (
    <>
      <AiEnabledGuard>
        {isV2 ? null : (
          <IconButton
            label="AI Assistant"
            tooltip="Assistant"
            icon="chat"
            size={size}
            onClick={() => {
              toggle('copilot');
            }}
            isNewLayout={props?.isNewLayout}
          />
        )}
        <History />
      </AiEnabledGuard>
      <SidebarChat />
    </>
  );
}

export { CopilotActions, CopilotInput, CopilotMessages, CopilotName, CopilotSupportButton } from './chat';
export { AgentBetaLabel } from './components/agent-beta-label';
export { CopilotSessionProvider, useCopilotChatContext, useCopilotSessionContext } from './components/session-provider';
export { Tour } from './components/tour';
export { AssistantFooter } from './components/assistant-footer';
export { AIShimmer } from './components/ai-shimmer';
export {
  CopilotSidebarProvider,
  useCopilotSidebar,
  useIsAgentFullscreen,
  useNavigateToAgentChat,
  useShouldShowAgentBar,
} from './hooks';
export {
  Chats as CopilotChats,
  ChatCountBar as CopilotChatCountBar,
  type ChatsLocation as CopilotChatsLocation,
} from './history';
export { useCopilotHistory } from './hooks';
